import { StI18nEntry } from '../../services/socialTradingService';

/** Fixed 14 locales — must match backend GraphQL I18nLang (zhHant ↔ storage zh-Hant). */
export const I18N_LANGS = [
  'ar',
  'de',
  'en',
  'es',
  'fr',
  'hi',
  'zhHant',
  'it',
  'ja',
  'ko',
  'pt',
  'tr',
  'vi',
  'zh',
] as const;

export type I18nLangCode = (typeof I18N_LANGS)[number];

export const I18N_LANG_LABELS: Record<I18nLangCode, string> = {
  ar: 'العربية (ar)',
  de: 'Deutsch (de)',
  en: 'English (en)',
  es: 'Español (es)',
  fr: 'Français (fr)',
  hi: 'हिन्दी (hi)',
  zhHant: '繁體中文 (zh-Hant)',
  it: 'Italiano (it)',
  ja: '日本語 (ja)',
  ko: '한국어 (ko)',
  pt: 'Português (pt)',
  tr: 'Türkçe (tr)',
  vi: 'Tiếng Việt (vi)',
  zh: '简体中文 (zh)',
};

export function emptyLangPack(zh = ''): StI18nEntry[] {
  return I18N_LANGS.map((lang) => ({ lang, text: lang === 'zh' ? zh : '' }));
}

/** Backend intel / i18n storage token (zhHant → zh-Hant). */
export function storageLang(code: I18nLangCode): string {
  return code === 'zhHant' ? 'zh-Hant' : code;
}

/** Display label for a stored or GraphQL lang token. */
export function langLabel(raw: string): string {
  const code = canonLang(raw);
  return code ? I18N_LANG_LABELS[code] : raw;
}

/** Form select value from API / storage lang token. */
export function formLang(raw: string): I18nLangCode {
  return canonLang(raw) || 'zh';
}

/** Map API / legacy langs onto I18N_LANGS codes (hk | zh-Hant → zhHant). */
function canonLang(lang: string): I18nLangCode | '' {
  const raw = String(lang ?? '').trim();
  if (
    raw === 'hk' ||
    raw === 'zhHant' ||
    raw === 'zh-Hant' ||
    raw === 'zh_Hant' ||
    raw.toLowerCase() === 'zhhant'
  ) {
    return 'zhHant';
  }
  const low = raw.toLowerCase();
  if ((I18N_LANGS as readonly string[]).includes(low)) {
    return low as I18nLangCode;
  }
  return '';
}

export function packFromEntries(entries?: StI18nEntry[] | null, zhFallback = ''): StI18nEntry[] {
  const map = new Map<string, string>();
  (entries || []).forEach((e) => {
    const lang = canonLang(e.lang);
    if (lang) map.set(lang, e.text ?? '');
  });
  return I18N_LANGS.map((lang) => ({
    lang,
    text: map.has(lang) ? String(map.get(lang) ?? '') : lang === 'zh' ? zhFallback : '',
  }));
}

/** Keep zh entry aligned with the Chinese primary scalar. */
export function syncZh(pack: StI18nEntry[], zh: string): StI18nEntry[] {
  return pack.map((e) => (e.lang === 'zh' ? { ...e, text: zh } : e));
}

/** Required packs: every non-zh locale must be non-empty (zh comes from primary). */
export function isLangPackComplete(pack: StI18nEntry[], allowEmpty: boolean): boolean {
  if (pack.length !== I18N_LANGS.length) return false;
  const seen = new Set<string>();
  const complete = pack.every((e) => {
    const lang = canonLang(e.lang);
    if (!lang) return false;
    if (seen.has(lang)) return false;
    seen.add(lang);
    if (!allowEmpty && lang !== 'zh' && !String(e.text ?? '').trim()) return false;
    return true;
  });
  return complete && seen.size === I18N_LANGS.length;
}

export function applyFillResult(
  pack: StI18nEntry[],
  entries: StI18nEntry[] | undefined,
  zhPrimary: string,
): StI18nEntry[] {
  return syncZh(packFromEntries(entries ?? pack, zhPrimary), zhPrimary);
}
